import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, BarChart3, TrendingUp, Award, Bell } from 'lucide-react';
import DashboardControls from './DashBoardControl';
import StockEntryForm from './StockEntryForm';
import SectorCards from './SectorCards';
import DashboardShowcase from '../components/DashboardShowcase/DashboardShowcase';
import axiosInstance from '../services/axiosInstance';
import Tour from 'reactour'; // Import the tour library

// ShadCN UI Components
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Dashboard = () => {
    const { user } = useOutletContext();
    const [showStockForm, setShowStockForm] = useState(false);
    const [isTourOpen, setIsTourOpen] = useState(false); // State to manage tour visibility
    const [showDashboardShowcase, setShowDashboardShowcase] = useState(true); // State to manage showcase visibility
    const [showcaseFadingOut, setShowcaseFadingOut] = useState(false); // State to manage fade-out animation
    const [showWelcomeCard, setShowWelcomeCard] = useState(true); // State to manage welcome card visibility
    const [welcomeCardFadingOut, setWelcomeCardFadingOut] = useState(false); // State to manage welcome card fade-out

    useEffect(() => {
        if (user && !user.user_guide) {
            setIsTourOpen(true); // Show tour if user_guide is false
        }
    }, [user]);

    // Auto-hide DashboardShowcase after 5 seconds with fade-out animation
    useEffect(() => {
        const fadeTimer = setTimeout(() => {
            setShowcaseFadingOut(true); // Start fade-out animation
        }, 4500); // Start fade-out at 4.5 seconds

        const hideTimer = setTimeout(() => {
            setShowDashboardShowcase(false);
        }, 5000); // Completely hide at 5 seconds

        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(hideTimer);
        }; // Cleanup timers on component unmount
    }, []);

    // Auto-hide Welcome Card after 5 seconds with fade-out animation
    useEffect(() => {
        const welcomeFadeTimer = setTimeout(() => {
            setWelcomeCardFadingOut(true); // Start fade-out animation
        }, 4500); // Start fade-out at 4.5 seconds

        const welcomeHideTimer = setTimeout(() => {
            setShowWelcomeCard(false);
        }, 5000); // Completely hide at 5 seconds

        return () => {
            clearTimeout(welcomeFadeTimer);
            clearTimeout(welcomeHideTimer);
        }; // Cleanup timers on component unmount
    }, []);

    const handleAddStock = () => {
        setShowStockForm(true);
    };

    const handleTourClose = async () => {
        setIsTourOpen(false);
        try {
            await axiosInstance.post('/auth/update-user-guide', { user_guide: true });
        } catch (error) {
            console.error('Error updating user guide status:', error);
        }
    };

    const tourSteps = [
        {
            selector: '.welcomeCard',
            content: 'Welcome to your dashboard! Here you can see an overview of your portfolio.',
            style: {
                backgroundColor: '#1e293b',
                color: '#ffffff',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
        },
        {
            selector: '.addStockButton',
            content: 'Click here to add a new stock to your portfolio.',
            style: {
                backgroundColor: '#1e293b',
                color: '#ffffff',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
        },
        {
            selector: '.sidebar',
            content: 'This is your sidebar where you can navigate through different sections.',
            style: {
                backgroundColor: '#1e293b',
                color: '#ffffff',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
        },
        {
            selector: '.stockEntryForm',
            content: 'Here you can add details about the stock you want to add.',
            style: {
                backgroundColor: '#1e293b',
                color: '#ffffff',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
        },
        // Add more steps as needed
    ];

    return (
        <div className="p-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                <div className="mb-4 sm:mb-0">
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent tracking-tight">
                        Your Portfolio
                    </h1>
                    <p className="text-slate-600 mt-2 text-lg">Track your investments and make informed decisions</p>
                </div>

                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="icon"
                        className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 hover:border-indigo-400 transition-all duration-200 rounded-xl"
                    >
                        <Bell className="h-5 w-5" />
                    </Button>
                </div>
                </div>

            {/* Dashboard Grid */}
            <div className="grid grid-cols-1 gap-6">
                {/* Welcome Card - shown for 5 seconds */}
                {showWelcomeCard && (
                    <Card className={`welcomeCard col-span-1 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white border-0 shadow-2xl overflow-hidden relative backdrop-blur-xl transition-opacity duration-500 ${welcomeCardFadingOut ? 'opacity-0' : 'opacity-100'}`}>
                        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-indigo-500/40 to-purple-600/40 rounded-full -mr-20 -mt-20"></div>
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-500/20 to-indigo-500/20 rounded-full -ml-16 -mb-16"></div>
                        <CardHeader className="relative z-10 p-8">
                            <div className="flex justify-between items-start">
                                <div>
                                    <CardTitle className="text-3xl text-white mb-3 font-bold tracking-tight">
                                        Welcome back, {user.name}! 👋
                                    </CardTitle>
                                    <p className="text-slate-200 text-lg leading-relaxed">
                                        Be a successful investor with StockKhata. Track your investments and make informed decisions.
                                    </p>
                                </div>
                                {user.premium && (
                                    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-2 rounded-xl text-sm font-semibold shadow-lg ring-2 ring-indigo-400/30 backdrop-blur-sm">
                                        <Award className="w-4 h-4 inline mr-2" />
                                        ✨ Premium
                                    </div>
                                )}
                            </div>
                        </CardHeader>
                    </Card>
                )}

                {/* Portfolio Analytics */}
                <Card className="col-span-1 shadow-xl border-0 bg-gradient-to-br from-white via-indigo-50/50 to-purple-50/50 backdrop-blur-sm ring-1 ring-indigo-200/30">
                    <CardHeader className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-indigo-200/30">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <CardTitle className="flex items-center gap-3 text-slate-900">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                                    <TrendingUp className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-xl font-bold tracking-tight">Your Portfolio by Sector</span>
                            </CardTitle>
                                <Button
                                    onClick={handleAddStock}
                                className="addStockButton bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 rounded-xl px-6 py-3 font-semibold"
                                >
                                <Plus className="w-5 h-5 mr-2" />
                                    Add Stock
                                </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <SectorCards />
                    </CardContent>
                </Card>
                </div>

            <StockEntryForm
                isOpen={showStockForm}
                onClose={() => setShowStockForm(false)}
                onSave={() => {
                    window.location.reload();
                }}
                className="stockEntryForm"
            />
            <Tour
                steps={tourSteps}
                isOpen={isTourOpen}
                onRequestClose={handleTourClose}
                accentColor="#6366f1"
                rounded={10}
                className="premium-tour"
                closeWithMask={false} // Ensure the close button is visible
            />
        </div>
    );
};

export default Dashboard;